/**
 * Design pillar source bar.
 *
 * Says where the pillar's decisions come from and what the index holds of
 * them: the design repository's `docs/design/` and `docs/adr/` folders
 * (design-pillar-reads-the-decisions; #571 named the folders), how many
 * decisions and design documents are indexed, when the repository was last
 * synced, and a Sync now. When the repository is not connected it says so
 * instead — a push to an unconnected repository is not indexed
 * (doc_sync_worker, #1350), so the list below would silently age.
 */

import { FolderGit2, RefreshCw } from 'lucide-react';
import { useConsoleUrl } from '@/hooks/useConsoleUrl';
import { DESIGN_FOLDERS, type DesignDecision } from '@/lib/designDecisions';

export interface DesignSource {
  /** The index's rows for the repo: null while loading, [] when it holds none. */
  decisions: DesignDecision[] | null;
  /** list_repos' word on the repo row; false means pushes are not indexed. */
  connected: boolean | undefined;
  lastSyncAt: string | null;
  syncing: boolean;
  onSync?: () => void;
}

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function Folders() {
  return (
    <>
      {DESIGN_FOLDERS.map((f, i) => (
        <span key={f}>
          {i > 0 && ' and '}
          <code className="px-1 rounded bg-slate-200 dark:bg-slate-800">{f}</code>
        </span>
      ))}
    </>
  );
}

export function DesignPublishBar({
  designRepo,
  capabilityDisabled = false,
  source,
}: {
  designRepo: string;
  /** The design capability is a deliberate opt-out (guided-repo-setup Phase D):
   *  show the decided fact instead of the configure-me nag. */
  capabilityDisabled?: boolean;
  source?: DesignSource;
}) {
  // Rendered on the capture surface (Docs tab); repo settings live on the
  // Build console, so the links are absolute cross-host URLs — hidden when
  // no tenant slug is resolvable.
  const consoleUrl = useConsoleUrl();
  if (capabilityDisabled) {
    return (
      <div className="mb-6 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400">
        <FolderGit2 className="w-4 h-4 shrink-0 text-slate-400" />
        <span>Design publishing is disabled for this workspace.</span>
        {consoleUrl && (
          <a href={consoleUrl('/settings/repo-roles')} className="ml-auto text-blue-600 dark:text-blue-400 hover:underline shrink-0">
            Enable in Settings
          </a>
        )}
      </div>
    );
  }
  if (!designRepo) {
    return (
      <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">
        <FolderGit2 className="w-4 h-4 shrink-0" />
        <span>
          No system design repository configured — SystemDox reads{' '}
          <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/40">docs/design/</code> and{' '}
          <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/40">docs/adr/</code> in the design repo.
        </span>
        {consoleUrl && (
          <a href={consoleUrl('/settings/repo-roles')} className="ml-auto font-medium underline hover:no-underline shrink-0">
            Configure
          </a>
        )}
      </div>
    );
  }
  if (source?.connected === false) {
    const last = when(source.lastSyncAt);
    return (
      <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">
        <FolderGit2 className="w-4 h-4 shrink-0" />
        <span>
          <strong>{designRepo}</strong> is not connected, so pushes to it are not indexed
          {last ? ` — the list below is the index of ${last}.` : ' — nothing of it is indexed yet.'}
        </span>
        {consoleUrl && (
          <a href={consoleUrl('/settings/github')} className="ml-auto font-medium underline hover:no-underline shrink-0">
            Connect
          </a>
        )}
      </div>
    );
  }

  const decisions = source?.decisions ?? null;
  const adrs = decisions ? decisions.filter((d) => d.kind === 'adr').length : 0;
  const designDocs = decisions ? decisions.length - adrs : 0;
  const last = when(source?.lastSyncAt ?? null);
  const counts = decisions === null
    ? 'Reading the index…'
    : decisions.length === 0
      ? (last ? 'Nothing under those folders' : 'Not indexed yet')
      : `${adrs} ${adrs === 1 ? 'decision' : 'decisions'} · ${designDocs} design ${designDocs === 1 ? 'doc' : 'docs'}`;

  return (
    <div className="mb-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <FolderGit2 className="w-4 h-4 shrink-0 text-slate-400" />
        <span className="text-sm text-slate-600 dark:text-slate-400">
          Reading <Folders /> in <strong className="text-slate-800 dark:text-slate-200">{designRepo}</strong>
        </span>
        <span className="ml-auto flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
          <span>
            {counts}
            {last && decisions && decisions.length > 0 ? ` · indexed ${last}` : ''}
          </span>
          {source?.onSync && (
            <button
              type="button"
              onClick={source.onSync}
              disabled={source.syncing}
              title="Re-read the repository and refresh this list"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${source.syncing ? 'animate-spin' : ''}`} />
              {source.syncing ? 'Syncing…' : 'Sync now'}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
